/*eslint-disable  @typescript-eslint/no-unused-vars*/

import React, { useRef, useEffect, useState } from 'react';
import './RmsSupportAIChatDashboard.css';
import { Icon } from '../../../globalUtils/globalIcons';
import CustomSearchBox from './CustomSearchBox';
import { fetchRmsSupportSearchQueryMatchingPrompts, fetchRmsSupportSearchQueryResponse, getReferenceDataReducer, getRmsSupportCenterReducer } from '../../../rmsReduxStore/reduxExports';
import { useAppDispatch, useAppSelector } from '../../../rmsReduxStore/rmsReduxHooks';
import { useEffectOnce } from '../../../globalUtils/globalHooks';
import { fetchAISupportSearchKeywordsReferenceData, fetchAISupportSearchPlaceholdersReferenceData } from '../../../rmsReduxStore/referenceDataRedux/referenceDataCreators';
import { useNavigate } from 'react-router-dom';
import { CustomButton, SidePopover } from '../../../globalUtils/globalExports';
import access from '../../../auth/service/AccessControl';
import { Id, toast } from 'react-toastify';
import ContactUsSidebar from '../ContactUsSidebar';
import CustomSidePopover from '../CustomSidePopover';

const RmsSupportAIChatDashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [queryText, setQueryText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const { aiSupportSearchPlaceholdersReferenceData, aiSupportCenterSearchKeywordsReferenceData } = useAppSelector(getReferenceDataReducer);
    const [activePlaceHolder, setActivePlaceHolder] = useState<string | undefined>(aiSupportSearchPlaceholdersReferenceData?.[0].value ?? 'Example: “Why is my charger offline?”');
    const [showKeywords, setShowKeywords] = useState(true);
    const [showMatchingPrompts, setShowMatchingPrompts] = useState(false);
    const { rmsSupportSearchQueryMatchingPrompts } = useAppSelector(getRmsSupportCenterReducer);
    const submitBtnRef = useRef<HTMLDivElement>(null);
    const searchBoxWrapperRef = useRef<HTMLDivElement>(null);
    const [isContactUsSidebarOpen,setIsContactUsSidebarOpen] = useState<boolean>(false);

    const errorToast = (message):Id => {
        toast.dismiss();
        return toast.error(message)
    };

    useEffect(() => {
        const updatePlaceHolder = () => {
            // Only update if input is empty
            if (queryText.length === 0) {
                const nextIndex = (currentIndex + 1) % (aiSupportSearchPlaceholdersReferenceData ? aiSupportSearchPlaceholdersReferenceData.length : 1);
                setActivePlaceHolder(aiSupportSearchPlaceholdersReferenceData?.[nextIndex].value);
                setCurrentIndex(nextIndex);
            }
        };

        const interval = setInterval(updatePlaceHolder, 5000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [currentIndex, aiSupportSearchPlaceholdersReferenceData, queryText.length]);

    useEffectOnce(() => {
        dispatch(fetchAISupportSearchPlaceholdersReferenceData())
        dispatch(fetchAISupportSearchKeywordsReferenceData())
        dispatch(fetchRmsSupportSearchQueryMatchingPrompts({ query: null}))
    })

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchBoxWrapperRef.current &&
                !searchBoxWrapperRef.current.contains(event.target as Node)
            ) {
                handleSearchBoxFocusBlured();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // const queryBoxResourcesButtons = [
    //     {
    //         id:'help-icon',
    //         className:'rms__support__ai__chatbot__dashboard-body-query-box-help-btn',
    //         iconClassName:'rms__support__help__btn__icon',
    //         onClick:()=>{}
    //     },
    //     {
    //         id:'troubleshoot-icon',
    //         className:'rms__support__ai__chatbot__dashboard-body-query-box-troubleshoot-btn',
    //         iconClassName:'rms__support__troubleshoot__btn__icon',
    //         onClick:()=>{}
    //     },
    //     {
    //         id:'documentation-icon',
    //         className:'rms__support__ai__chatbot__dashboard-body-query-box-documentation-btn',
    //         iconClassName:'rms__support__documentation__btn__icon',
    //         onClick:()=>{}
    //     }
    // ] 

    const resourcesCards = [
        {
            iconClassName: 'rms__help__faq__card__icon',
            header: 'Help and FAQ',
            field: 'helpAndFaq',
            navLink: '/help-and-faq-dashboard',
            description: 'Get quick answer & guidance on using the platform.',
            className: 'rms__help__faq__card',
            isAccessible: true,
            onClick: ()=>{navigate('/help-and-faq-dashboard')}
        },
        {
            iconClassName: 'rms__troubleshoot__card__icon',
            header: 'Troubleshooting',
            field: 'troubleshooting',
            navLink: '/troubleshooting',
            isAccessible: true,
            description: 'Your first steps to diagnose and fix common issues.',
            onClick: ()=>{navigate('/troubleshooting')}
        },
        {
            iconClassName: 'rms__documentation__card__icon',
            header: 'Documents Hub',
            field: 'documentation',
            navLink: '/documentation',
            isAccessible: (!access?.isOrgTypeCustomer() || access?.aiSupportCenter?.documentation?.view()),
            description: 'Your central hub for product documents and releases.', 
            onClick:()=>{
                if(access?.aiSupportCenter?.documentation?.view()) {
                    navigate('/documentation')
                }
                else {
                    errorToast('Access to Documents Hub is restricted.')
                }
            }
        }
    ]


    const contactUsBtn = {
        id:'rms_support__centre-contact-us-btn',
        className:'rms_support__centre__contact__us__btn',
        text:'Contact us',
        variant:'filled',
        iconClassName:'rms__support__help__btn__icon',
        onClick:()=>{
            setIsContactUsSidebarOpen(true);
        }
    }


    const handleSearchQueryChange = (value: string) => {
        if (value?.length === 0) {
            dispatch(fetchRmsSupportSearchQueryMatchingPrompts({ query: null }));
        } else {
            setShowKeywords(false);
            setShowMatchingPrompts(true);
            dispatch(fetchRmsSupportSearchQueryMatchingPrompts({ query: value?.trim() }));
        }

        setQueryText(value);
    }

    const handleSubmitBtnClick = () => {
        if (!queryText || queryText.trim() === '') return;
        // remove trailing \n
        const cleanText = queryText.split('\n').filter(line => line.trim() !== '').join('\n')
        dispatch(fetchRmsSupportSearchQueryResponse({ query: cleanText, context:[] }))
        navigate('/rms-support-center/chat', {
            state: {
                queryObject: {
                    id: '',
                    query: cleanText,
                    result: null
                }
            }
        })
    }

    //To simulate submit click when user pressess enter
    const handleEnterKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            submitBtnRef?.current?.click();
        }
    }

    const handleSearchBoxFocused = () => {
        setShowMatchingPrompts(true);
        setShowKeywords(false);
    }
    const handleSearchBoxFocusBlured = () => {
        setShowMatchingPrompts(false);
        setShowKeywords(true);
    }

    return (
        <div className='rms__support__ai__chatbot__dashboard__container'>
            <div className='rms__support__ai__chatbot__dashboard-header'>
                <div className='rms__support__ai__chatbot__dashboard-heading'>
                    <div className='rms__support__ai__chatbot__dashboard-heading-icon'>
                        <Icon
                            containerClassName='rms__support__ai__chatbot__dashboard-heading-icon-container'
                            iconClassName='rms__support__ai__chatbot__icon'
                        />
                    </div>
                    <div className='rms__support__ai__chatbot__dashboard-module-label'>
                        AI Smart Assist
                    </div>
                    <div className='rms__support__ai__chatbot__dashboard-heading-text'>
                        Hey there! <span className='rms__support__ai__chatbot__dashboard-heading-sub-text'>What can I help you solve right now?</span>
                    </div>
                </div>
            </div>
            <div className='rms__support__ai__chatbot__dashboard-body'>
                <div className='rms__support__ai__chatbot__dashboard-body-top' ref={searchBoxWrapperRef}>
                    <div className='rms__support__ai__chatbot__dashboard-body-query-box-container'>
                        <div className='rms__support__ai__chatbot__dashboard-body-query-box'>
                            <div className='rms__support__ai__chatbot__dashboard-body-query-box-top'>
                                <div className='rms__support__ai__chatbot__dashboard-body-query-box-text-section'
                                    onKeyDown={handleEnterKeyDown}>
                                    <CustomSearchBox
                                        inputFieldId={''}
                                        isRequired={false}
                                        inputValue={queryText}
                                        handleInputChange={(e) => { handleSearchQueryChange(e.target.value) }}
                                        isResizeable={false}
                                        className={''}
                                        maxHeight={300}
                                        inputPlaceHolder={activePlaceHolder}
                                        enterToSubmit={true}
                                        handleSearchBoxFocused={handleSearchBoxFocused}
                                        // handleSearchBoxFocusBlured={handleSearchBoxFocusBlured}
                                    />
                                </div>
                                <div className='rms__support__ai__chatbot__dashboard-body-query-box-btn-container'>
                                    {/* <div className='rms__support__ai__chatbot__dashboard-body-query-box-btn-container-left'>
                                    {queryBoxResourcesButtons.map(btn=>{
                                       return( 
                                       <div key={btn.id} className={btn.className} onClick={btn.onClick}>
                                         <Icon  iconClassName={btn.iconClassName}/>
                                        </div> 
                                       );
                                    })}       
                                </div>  */}
                                    <div className='rms__support__ai__chatbot__dashboard-body-query-box-btn-container-right'>
                                        {/* <div className='rms__support__ai__chatbot__dashboard-body-query-box-btn-container-right-voice-input-btn'>
                                            <Icon iconClassName='rms__support__voice__input__icon' />
                                        </div> */}
                                        <div className='rms__support__ai__chatbot__dashboard-body-query-box-btn-container-right-submit-btn'
                                            ref={submitBtnRef}
                                            onClick={handleSubmitBtnClick}>
                                            <Icon iconClassName='rms__support__query__submit__icon' />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {
                                showMatchingPrompts && rmsSupportSearchQueryMatchingPrompts && rmsSupportSearchQueryMatchingPrompts?.length > 0 && <div className='rms__support__ai__chatbot__dashboard-body-query-box-matching-prompts-container'>
                                    {
                                        rmsSupportSearchQueryMatchingPrompts?.map((prompt, index) => {
                                            const processedText = queryText?.toLowerCase();
                                            if(!prompt?.value?.startsWith(processedText))return;
                                            return (
                                                <div key={prompt?.id + index } className={index == rmsSupportSearchQueryMatchingPrompts?.length - 1 ? 'rms__support__ai__chatbot-matching-prompts-last' : 'rms__support__ai__chatbot-matching-prompts'}
                                                    onClick={() => { handleSearchQueryChange(prompt?.value) }}>
                                                    <span className='rms__support__ai__chatbot_prompt-matched-part'>{processedText}</span>
                                                    <span className='rms__support__ai__chatbot_prompt-unmatched-part'>{prompt?.value?.replace(processedText, '')}</span>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            }
                        </div>
                    </div>
                    {
                        showKeywords && aiSupportCenterSearchKeywordsReferenceData && <div className='rms__support__ai__chatbot__dashboard-body-search-keywords-container'>
                            {
                                aiSupportCenterSearchKeywordsReferenceData?.map(keyword => {
                                    return (
                                        <div key={keyword?.id} className='rms__support__ai__chatbot__search-keyword'
                                            onClick={() => {
                                                handleSearchQueryChange(keyword?.value)
                                            }}> {keyword?.value}</div>
                                    )
                                })
                            }
                            {/* <div key='see-more-btn' className='rms__support__ai__chatbot__search-keyword'
                            > + See more </div> */}
                        </div>
                    }
                </div>
                {
                    showKeywords && <div className='rms__support__ai__chatbot__dashboard-body-bottom'>
                        <div className='rms__support__ai__chatbot__dashboard-body-resources-container'>
                            <div className='rms__support__ai__chatbot__dashboard-body-resources-container-header'>
                                <div className='rms__support__ai__chatbot__dashboard-body-resources-container-header-icon'>
                                    <Icon iconClassName='rms__support__ai__chatbot__dashboard-body-resources-header-icon' />
                                </div>
                                <div className='rms__support__ai__chatbot__dashboard-body-resources-container-header-heading'>
                                    Help and Resources
                                </div>
                            </div>
                            <div className='rms__support__ai__chatbot__dashboard-body-resources-container-body'>
                                {
                                    resourcesCards.map(card => {
                                        if(!card.isAccessible)return;
                                        return (
                                            <div key={card?.field} className={'rms__support__ai__resources__cards ' + card?.className}
                                                onClick={card?.onClick}
                                            >
                                                <div className='rms__support__ai__resources__card-icon'>
                                                    <Icon iconClassName={card?.iconClassName} />
                                                </div>
                                                <div className='rms__support__ai__resources__card-header'>{card?.header}</div>
                                                <div className='rms__support__ai__resources__card-description'>{card?.description}</div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            <div className='rms__support__ai__chatbot__dashboard-contact-us-btn'>
                <CustomButton 
                buttonId={contactUsBtn?.id}
                buttonVariant={contactUsBtn?.variant}
                buttonText={contactUsBtn?.text} 
                buttonIconClassName={contactUsBtn?.iconClassName}
                btnClassName={contactUsBtn?.className}
                handleClick={contactUsBtn?.onClick}
                handleKeyPress={()=>{}}
                />
            </div>
            <>
                {
                    isContactUsSidebarOpen && <CustomSidePopover
                    id={'ai__smart__assisst__contact__us__popup'} 
                    open={isContactUsSidebarOpen} 
                    handleClose={()=>{setIsContactUsSidebarOpen(false)}}
                    /*eslint-disable react/no-children-prop*/
                    children={
                        <ContactUsSidebar/>
                    }/>
                }
            </>
        </div>
    )
}

export default RmsSupportAIChatDashboard